import type { ReactNode } from 'react'
import clsx from 'clsx'
import type { LucideIcon } from 'lucide-react'

import { usePageTitle } from '@/shared/hooks/usePageTitle'
import { PageIconBadge, type PageIconTone } from './PageIconBadge'

type Props = {
  icon: LucideIcon
  tone: PageIconTone
  title: ReactNode
  subtitle?: ReactNode
  /** 为 true 时拼接租户品牌前缀(模块页主标题);默认 false */
  prefixed?: boolean
  /** Filters or extra controls below subtitle, still in the title column */
  extra?: ReactNode
  children?: ReactNode
  className?: string
}

export function PageHeading({
  icon,
  tone,
  title,
  subtitle,
  prefixed = false,
  extra,
  children,
  className,
}: Props) {
  const { withPrefix } = usePageTitle()
  const displayTitle =
    prefixed && typeof title === 'string' ? withPrefix(title) : title

  return (
    <div className={clsx('mb-6 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between', className)}>
      <div className="flex min-w-0 items-start gap-3 sm:gap-4">
        <PageIconBadge icon={icon} tone={tone} size="md" className="mt-0.5 shrink-0" />
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-zinc-50 sm:text-2xl">{displayTitle}</h1>
          {subtitle ? <p className="mt-1 text-sm text-zinc-500">{subtitle}</p> : null}
          {extra ? <div className="mt-4">{extra}</div> : null}
        </div>
      </div>
      {children ? (
        <div className="flex w-full min-w-0 flex-col gap-3 lg:w-auto lg:max-w-full lg:items-end">
          {children}
        </div>
      ) : null}
    </div>
  )
}
